import { useMemo, useState } from 'react'

import { Box, EnhancedDimension, Icon, Text } from '@factorialco/deprecated-design-system'
import { EntityId, EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import { WorkspacesSelectorDocument, WorkspacesSelectorQuery } from 'generated/resources/graphql'

import { EntitySelectWrapper } from 'modules/core/components/EntitySelectWrapper'
import LabeledInput from 'modules/core/components/LabeledInput'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

export interface Location {
  id: string
  name: string
}

// Extend EntitySelectEntity to include metadata and ensure id property exists
interface LocationEntitySelectEntity extends EntitySelectEntity {
  id: string
  metadata: Location
}

interface LocationSelectorProps {
  value?: Location | null
  onSelect: (location: Location | null) => void
  label?: string
  emptyLabel?: string
  mandatory?: boolean
  disabled?: boolean
  error?: boolean
  width?: string
  minWidth?: string
}

const mapWorkspacesToEntities = (workspaces: Location[]): LocationEntitySelectEntity[] => {
  return workspaces.map((workspace) => ({
    id: workspace.id,
    name: workspace.name,
    subtitle: '', // Could add workspace type/capacity if available
    metadata: workspace,
  }))
}

const mapToLocation = (entity: LocationEntitySelectEntity): Location => {
  const metadata = entity.metadata
  return {
    id: String(entity.id),
    name: metadata.name,
  }
}

// Type guard to check if an EntitySelectEntity is a LocationEntitySelectEntity
const isLocationEntity = (entity: EntitySelectEntity): entity is LocationEntitySelectEntity => {
  return typeof entity.id === 'string' && 'metadata' in entity && entity.metadata !== null
}

export const LocationSelector = ({
  value,
  onSelect,
  label,
  emptyLabel,
  mandatory = false,
  disabled = false,
  error = false,
  width,
  minWidth,
}: LocationSelectorProps) => {
  const [skip, setSkip] = useState<boolean>(true)
  const [expandedElements, setExpandedElements] = useState<EntityId[]>([])
  const { debouncedSearch } = useDebouncedSearch()

  const onOpen = () => {
    if (skip) setSkip(false)
  }

  // Load workspaces - the backend should already filter by company through the boundary
  const { data: workspacesData, loading: workspacesLoading } = useQuery<
    WorkspacesSelectorQuery,
    Record<string, never>
  >(WorkspacesSelectorDocument, {
    skip,
  })

  const workspaces = useMemo(() => {
    const workspaceNodes = workspacesData?.spaceControl?.workSpacesConnection?.nodes || []

    const allWorkspaces = workspaceNodes.map(
      (node: WorkspacesSelectorQuery['spaceControl']['workSpacesConnection']['nodes'][0]) => ({
        id: String(node?.id || ''),
        name: node?.name || '',
      })
    )

    // Client-side filtering since workSpacesConnection doesn't support search
    if (!debouncedSearch) {
      return allWorkspaces
    }

    return allWorkspaces.filter((workspace: Location) =>
      `${workspace.name}`.toLowerCase().includes(debouncedSearch.toLowerCase())
    )
  }, [workspacesData?.spaceControl?.workSpacesConnection?.nodes, debouncedSearch])

  const workspaceEntities = useMemo(() => mapWorkspacesToEntities(workspaces), [workspaces])

  const selectedLocation = useMemo(
    () =>
      value
        ? [
            {
              id: value.id,
              name: value.name,
              subtitle: '',
              metadata: value,
            },
          ]
        : [],
    [value]
  )

  const handleOnSelectSingle = (selection: EntitySelectEntity | null) => {
    if (selection && isLocationEntity(selection)) {
      onSelect(mapToLocation(selection))
    } else {
      onSelect(null)
    }
  }

  // Helper to validate dimensions
  const getValidDimension = (dim?: string): EnhancedDimension | undefined => {
    if (!dim) return undefined
    if (dim === 'full' || dim === 'none') return dim

    // Type guard to validate size dimensions
    if (dim.startsWith('s') && /^s\d+$/.test(dim)) {
      // Use switch statement for type narrowing without assertions
      switch (dim) {
        case 's16':
        case 's24':
        case 's32':
        case 's40':
        case 's48':
        case 's64':
        case 's80':
        case 's96':
          return dim
        default:
          break
      }
    }
    return undefined
  }

  return (
    <Box
      minWidth={getValidDimension(minWidth)}
      width={getValidDimension(width ?? minWidth ?? 'full')}
      position="relative"
    >
      <EntitySelectWrapper
        label={i18n.t('it_management.location_selector.select_location')}
        hideLabel
        loading={workspacesLoading}
        onOpen={onOpen}
        entities={workspaceEntities}
        selectedGroup="all"
        selectedGroupedEntities={selectedLocation}
        onGroupChange={() => {}}
        onSelectExpandedElements={setExpandedElements}
        expandedElements={expandedElements}
        searchPlaceholder={i18n.t('it_management.location_selector.search_placeholder')}
        selectAllLabel={i18n.t('it_management.location_selector.select_all_label')}
        clearLabel={i18n.t('it_management.location_selector.clear_label')}
        selectedLabel={i18n.t('it_management.location_selector.selected_label')}
        notFoundSubtitle={i18n.t('it_management.location_selector.not_found_subtitle')}
        notFoundTitle={i18n.t('it_management.location_selector.not_found_title')}
        placeholder={i18n.t('it_management.location_selector.trigger_placeholder')}
        selectedItemsCopy={i18n.t('it_management.location_selector.trigger_selected')}
        groups={[{ label: i18n.t('it_management.common.all'), value: 'all' }]}
        singleSelector
        onSelect={handleOnSelectSingle}
        onSelectMultiple={() => {}}
        minWidth={getValidDimension(minWidth)}
        width={getValidDimension(width)}
        disabled={disabled}
      >
        <Box flexDirection="row" width="full">
          <LabeledInput
            aria-label="location-selector"
            readonly={disabled}
            error={error ? i18n.t('it_management.common.error_occurred') : undefined}
            mandatory={mandatory}
            label={label}
            value={value?.name ?? undefined}
          >
            <Box
              paddingX="s16"
              paddingY="s8"
              flexDirection="row"
              height="s40"
              alignItems="center"
              width="full"
              cursor={disabled ? 'default' : 'pointer'}
            >
              <Box flexDirection="row" width="full" gap="s6">
                <Icon.Medium icon="search" color={disabled ? 'grey600' : 'grey800'} />
                <Box overflowX="hidden" textAlign="left">
                  <Text.Normal ellipsis>
                    {value ? value.name : emptyLabel ?? 'Select location'}
                  </Text.Normal>
                </Box>
              </Box>
              <Icon.Small icon="caret-down" />
            </Box>
          </LabeledInput>
        </Box>
      </EntitySelectWrapper>
    </Box>
  )
}
